<template>
  <div class="form-group input-group-sm">
    <base-help-tooltip
      class="mb-3"
      popover-placement="topright"
    >
      <template v-slot:left>
        <p
          class="mb-0 form-label"
        >
          {{ capitalizeFirstLetter(logoType) }} Logo
        </p>
      </template>
      <template v-slot:popover-body>
        <p
          class="mb-0"
        >
          <template v-if="logoType === 'unit'">
            Logos uploaded here will be applied only to this creative template.
          </template>
          <template v-else-if="logoType === 'master'">
            Logos uploaded here will apply to all ad units.
          </template>
        </p>
      </template>
    </base-help-tooltip>
    <b-row
      class="gutters-0 d-flex justify-content-between"
    >
      <b-col
        cols="auto"
        class="logo-upload"
        @mouseenter="isHoveringOverUploadButton = true"
        @mouseleave="isHoveringOverUploadButton = false"
      >
        <img
          v-if="logoUrl"
          :src="logoUrl"
          class="logo-img bg-white btn-secondary rounded"
          :class="{'disabled': disabled}"
          height="100%"
          width="100%"
          @click="openLogoModal()"
        >
        <button
          v-show="!logoUrl || (logoUrl && isHoveringOverUploadButton)"
          type="button"
          :class="['btn btn-block btn-secondary logo-btn-width w-100 h-100', {'custom-upload-logo-btn': !logoUrl || isHoveringOverUploadButton}]"
          @click="openLogoModal()"
          data-test="upload-logo-button"
          :disabled="disabled"
        >
          <i class="fal fa-upload mr-2" />
          <template v-if="!logoUrl">
            Upload logo
          </template>
          <template v-else-if="!GET_IS_CAMPAIGN_READONLY">
            Replace logo
          </template>
        </button>
      </b-col>
      <b-col
        cols="2"
        class="ml-2"
      >
        <button
          type="button"
          class="btn btn-block btn-danger py-4"
          @click="deleteLogo()"
          :disabled="disabled || !this.logoUrl"
        >
          <i class="fal fa-trash-can" />
        </button>
      </b-col>
    </b-row>
    <upload-logo-modal
      :title="getModalTitle()"
      :logo-type="logoType"
      :logo-url="logoUrl"
      @closeModal="closeModal(...arguments)"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

import UploadLogoModal from '@/modules/content/components/CreativesPage/Sidebar/UploadLogoModal';

import textHelper from '@/core/mixins/textHelper';

export default {
  name: 'LogoUpload',
  mixins: [textHelper],
  components: {
    UploadLogoModal,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    logoType: {
      type: String,
      default: 'template',
    },
  },
  data() {
    return {
      isHoveringOverUploadButton: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'campaignProperties',
    ]),
    ...mapState('content', [
      'creative',
      'selectedCreativeId',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CURRENT_SELECTED_CREATIVE_PROPERTIES,
    ]),
    hasMasterLogo() {
      return !!this.campaignProperties.logo;
    },
    hasTemplateLogo() {
      return !!this.GET_CURRENT_SELECTED_CREATIVE_PROPERTIES.logo;
    },
    logoUrl() {
      if (this.hasTemplateLogo && this.logoType === 'unit') return this.GET_CURRENT_SELECTED_CREATIVE_PROPERTIES.logo;
      if (this.hasMasterLogo && this.logoType === 'master') return this.campaignProperties.logo;
      return null;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.DELETE_LOGO_IMAGE,
      contentTypes.TRIGGER_CREATIVE_RERENDER,
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
    ]),
    getModalTitle() {
      return `${this.capitalizeFirstLetter(this.logoType)} Logo Upload`;
    },
    openLogoModal() {
      if (!this.logoType) return;
      this.SET_IS_SIDEBAR_INPUT_FOCUSED(true);
      this.$root.$emit('bv::show::modal', `UploadLogoModal-${this.logoType}-${this.selectedCreativeId}`);
    },
    closeModal() {
      this.$root.$emit('bv::hide::modal', `UploadLogoModal-${this.logoType}-${this.selectedCreativeId}`);
      this.SET_IS_SIDEBAR_INPUT_FOCUSED(false);
    },
    deleteLogo() {
      const payload = {
        advertiserId: this.campaign.advertiserId,
        campaignId: this.campaign.id,
        creativeId: this.selectedCreativeId,
        type: this.logoType,
      };
      this.DELETE_LOGO_IMAGE(payload)
        .then(() => {
          this.TRIGGER_CREATIVE_RERENDER();
        });
    },
  },
};
</script>
<style lang="scss" scoped>
.logo-upload {
  position: relative;
  flex-grow: 1;
  &:hover {
    cursor: pointer;
  }
}
.custom-upload-logo-btn {
  &:hover:not(:disabled) {
    color: $primary;
    background-color: $white;
    border: 1px solid $primary;
  }
}
.logo-img {
  position: absolute;
  top: 0;
  left: 0;
  object-fit: contain;
  &:hover:not(.disabled) {
    opacity: 20%;
  }
  &.disabled {
    cursor: not-allowed;
  }
}
</style>
